<template>
    <details class="panneau-adr" :open="ouvert">
        <summary class="panneau-adr__entete">
            <span class="panneau-adr__titre">{{ titre }}</span>
            <span class="badge" :class="badgeClasse">ADR : {{ badgeTexte }}</span>
            <span v-if="imtEtat" class="badge" :class="imtEtat.cls">IMT : {{ imtEtat.badge }}</span>
        </summary>

        <div class="panneau-adr__corps">
            <!-- ===== ADR ===== -->
            <div v-if="enErreur" class="panneau-adr__erreur">
                <strong>Erreur ADR</strong>
                <pre>{{ erreur || "Le service ADR a retourne une erreur." }}</pre>
            </div>
            <div v-if="resultat" class="panneau-adr__resultat">
                <pre>{{ resultat }}</pre>
            </div>
            <p v-else-if="!enErreur" class="panneau-adr__vide">
                Aucun resultat ADR pour ce beneficiaire.
            </p>

            <!-- ===== IMT (medecin traitant) ===== -->
            <div v-if="imtEtat" class="panneau-adr__imt">
                <div class="panneau-adr__imt-titre">Medecin traitant (IMT)</div>
                <div v-if="imtEtat.erreur" class="panneau-adr__erreur">
                    <strong>Erreur IMT</strong>
                    <pre>{{ imtEtat.erreur }}</pre>
                </div>
                <p v-else class="panneau-adr__imt-info">{{ imtEtat.info }}</p>
            </div>
        </div>
    </details>
</template>

<script>
    import { defineComponent } from 'vue';

    export default defineComponent({
        name: 'PanneauADR',
        props: {
            resultat: { type: String, default: "" },
            statut: { type: String, default: "" },
            erreur: { type: String, default: "" },
            // Resultat IMT (store ChoixBenecStore/getMedecinIMT) : { statutExec, statutResult, code,
            // message, erreurTexte, nom, prenom, ... } ou null.
            medecinIMT: { type: Object, default: null },
            titre: { type: String, default: "Teleservices (ADR / IMT)" },
        },
        computed: {
            enErreur() {
                const s = (this.statut || "").toLowerCase();
                // Statuts consideres comme "a montrer en rouge" (cf desktop afficherErreurAdr).
                return s.includes("erreur") || s.includes("nonlance")
                    || s.includes("refus") || !!this.erreur;
            },
            // Etat IMT a afficher (badge + info/erreur). null = rien a montrer (IMT non lance).
            imtEtat() {
                const m = this.medecinIMT;
                if (!m) return null;
                const exec = m.statutExec || "";
                if (!m.code && (exec === "" || exec === "NonLance" || exec === "NonLancé")) return null;
                if (exec === "EnCoursDExecution") return { cls: "badge--neutre", badge: "En cours", info: "Interrogation du medecin traitant en cours...", erreur: "" };
                if (m.code === "00" || (m.statutResult || "") === "MédecinTrouvé") {
                    const nom = ((m.nom || "") + " " + (m.prenom || "")).trim();
                    return { cls: "badge--ok", badge: "Trouve", info: nom ? ("Medecin traitant : " + nom) : "Medecin traitant trouve", erreur: "" };
                }
                if (m.code === "10" || (m.statutResult || "") === "MédecinNonTrouvé") {
                    return { cls: "badge--warn", badge: "Aucun", info: "Aucun medecin traitant declare pour ce beneficiaire.", erreur: "" };
                }
                return { cls: "badge--erreur", badge: "Erreur", info: "", erreur: m.erreurTexte || m.message || "Le service IMT a retourne une erreur." };
            },
            enOpposition() {
                return (this.statut || "").toLowerCase().includes("opposition");
            },
            // Ouvert d'office quand il y a une erreur ADR OU IMT (l'utilisateur doit la voir).
            ouvert() {
                return this.enErreur || (this.imtEtat && this.imtEtat.cls === "badge--erreur");
            },
            badgeTexte() {
                if (this.enErreur) return "Erreur";
                if (this.enOpposition) return "Opposition";
                if ((this.statut || "").toLowerCase().includes("succes")) return "Succes";
                return this.statut || "ADR";
            },
            badgeClasse() {
                if (this.enErreur) return "badge--erreur";
                if (this.enOpposition) return "badge--warn";
                if ((this.statut || "").toLowerCase().includes("succes")) return "badge--ok";
                return "badge--neutre";
            },
        },
    });
</script>

<style scoped>
    .panneau-adr {
        border: 1px solid #d0d7de;
        border-radius: 8px;
        background: #fff;
        margin: 8px 0;
    }

    .panneau-adr__entete {
        cursor: pointer;
        padding: 10px 12px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        font-weight: 600;
        list-style: none;
    }

        .panneau-adr__entete::-webkit-details-marker {
            display: none;
        }

    .panneau-adr__titre {
        color: #004AAD;
    }

    .panneau-adr__corps {
        padding: 0 12px 12px;
    }

    .panneau-adr__resultat pre,
    .panneau-adr__erreur pre {
        white-space: pre-wrap;
        word-break: break-word;
        background: #F4EDFF;
        padding: 10px;
        border-radius: 6px;
        margin: 6px 0 0;
        font-family: ui-monospace, Menlo, Consolas, monospace;
        font-size: 13px;
        max-height: 420px;
        overflow: auto;
    }

    .panneau-adr__erreur pre {
        background: #fee2e2;
        color: #7f1d1d;
    }

    .panneau-adr__vide {
        color: #6b7280;
        font-size: 13px;
    }

    .panneau-adr__imt {
        margin-top: 10px;
        padding-top: 10px;
        border-top: 1px dashed #d0d7de;
    }

    .panneau-adr__imt-titre {
        font-weight: 600;
        color: #004AAD;
        margin-bottom: 4px;
    }

    .panneau-adr__imt-info {
        color: #374151;
        font-size: 13px;
        margin: 4px 0 0;
    }

    .badge {
        font-size: 12px;
        padding: 2px 10px;
        border-radius: 999px;
        font-weight: 700;
    }

    .badge--ok {
        background: #dcfce7;
        color: #166534;
    }

    .badge--erreur {
        background: #fee2e2;
        color: #b91c1c;
    }

    .badge--warn {
        background: #ffedd5;
        color: #9a3412;
    }

    .badge--neutre {
        background: #f3f4f6;
        color: #374151;
    }
</style>
